/**
 * AlertImageGallery — inline image strip for an alert in the feed.
 *
 * Shows a thumbnail strip with an "📷 N" badge. Clicking a thumb opens a
 * fullscreen lightbox. The alert owner (and admins) get an "Add" tile to
 * upload more photos after the alert is live (up to 3 total). Images are
 * lazily fetched the first time the user expands the gallery, since the
 * list endpoint only ships an `images_count` to keep payloads small.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import AlertImageLightbox from "./alerts/AlertImageLightbox";
import AlertImageThumbs from "./alerts/AlertImageThumbs";
import AlertImageUploadPanel from "./alerts/AlertImageUploadPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES_PER_ALERT = 3;

export default function AlertImageGallery({ alert, user, onChange }) {
  const initialCount = alert.images_count || 0;
  const canEdit =
    !!user &&
    alert.status !== "resolved" &&
    (alert.user_id === user.user_id || user.role === "system_admin" || user.role === "govt_admin");
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);    // base64 data URLs
  const [loading, setLoading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [showAdder, setShowAdder] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/alerts/${alert.alert_id}/images`, { withCredentials: true });
      setImages(r.data?.images || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load photos");
    } finally {
      setLoading(false);
    }
  }, [alert.alert_id]);

  // First time the user expands, fetch the actual images. Subsequent toggles
  // don't re-fetch unless the count changes (e.g. another user added one).
  useEffect(() => {
    if (open && images.length === 0 && initialCount > 0 && !loading) {
      fetchImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleUploaded = async (newCount) => {
    setShowAdder(false);
    // Refresh images + bubble new count up to parent so the feed badge updates.
    await fetchImages();
    onChange?.({
      alert_id: alert.alert_id,
      images_count: newCount ?? images.length,
    });
  };

  // Effective count for the badge — pending adds aren't sent yet, so we
  // only count what's on the server.
  const shownCount = images.length || initialCount;
  if (shownCount === 0 && !canEdit) return null;

  if (!open) {
    return (
      <div className="mt-1.5" data-testid={`alert-gallery-${alert.alert_id}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[10px] text-gray-600 hover:text-gray-900 font-body inline-flex items-center gap-1"
          data-testid={`alert-gallery-toggle-${alert.alert_id}`}
        >
          <Camera className="w-3 h-3" />
          {shownCount > 0
            ? `${shownCount} photo${shownCount === 1 ? "" : "s"}`
            : "Add photos"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1.5" data-testid={`alert-gallery-${alert.alert_id}`}>
      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[10px] text-gray-500 hover:text-gray-900 font-body inline-flex items-center gap-1"
          data-testid={`alert-gallery-collapse-${alert.alert_id}`}
        >
          <Camera className="w-3 h-3" /> Hide photos
        </button>
        {loading ? (
          <div className="text-[10px] text-gray-400 font-body inline-flex items-center gap-1 py-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading photos…
          </div>
        ) : (
          <AlertImageThumbs
            alertId={alert.alert_id}
            images={images}
            canAdd={canEdit && images.length < MAX_IMAGES_PER_ALERT && !showAdder}
            onOpenLightbox={setLightboxIdx}
            onShowAdder={() => setShowAdder(true)}
          />
        )}

        {showAdder && (
          <AlertImageUploadPanel
            alertId={alert.alert_id}
            maxRemaining={MAX_IMAGES_PER_ALERT - images.length}
            onUploaded={handleUploaded}
            onCancel={() => setShowAdder(false)}
          />
        )}
      </div>

      {lightboxIdx !== null && images[lightboxIdx] && (
        <AlertImageLightbox
          src={images[lightboxIdx]}
          index={lightboxIdx}
          total={images.length}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}
